import { toast } from "react-toastify";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Description,
} from "@headlessui/react";
import { ClipLoader } from "react-spinners";
import { IoMdAdd } from "react-icons/io";
import { MdEdit, MdOutlineDelete } from "react-icons/md";

import { DarkModeContext } from "../../Context/DarkModeContext";
import axios from "../../Context/axiosInstance";

const errorMessage = (error, fallback) => {
  const data = error.response?.data;
  if (typeof data === "string" && data) return data;
  const reasons = Array.isArray(data?.errors)
    ? data.errors
        .map((item) => item.reason)
        .filter(Boolean)
        .join(" ")
    : "";
  return (
    reasons ||
    data?.detail ||
    data?.message ||
    (error.response?.status === 409
      ? "An academic level with this name already exists."
      : fallback)
  );
};

export default function AdminAcademicLevels() {
  const { isDarkMode } = useContext(DarkModeContext);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [name, setName] = useState("");
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const busy = useRef(false);
  const request = useRef(0);
  const load = useCallback(async () => {
    const id = ++request.current;
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get("/academic-levels");
      if (id === request.current) setLevels(data);
    } catch (err) {
      if (id === request.current)
        setError(
          errorMessage(
            err,
            "Could not load academic levels. Please try again.",
          ),
        );
    } finally {
      if (id === request.current) setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
    return () => {
      request.current += 1;
    };
  }, [load]);
  const open = (mode, level = null) => {
    setModal({ mode, level });
    setName(level?.name || "");
    setFormError("");
    setNotice("");
  };
  const close = () => {
    if (!busy.current) setModal(null);
  };
  const submit = async (event) => {
    event.preventDefault();
    if (busy.current) return;
    if (modal.mode !== "delete" && !name.trim()) {
      setFormError("Name is required.");
      return;
    }
    busy.current = true;
    setSaving(true);
    setFormError("");
    try {
      if (modal.mode === "add")
        await axios.post("/academic-levels", { name: name.trim() });
      else if (modal.mode === "edit")
        await axios.put(`/academic-levels/${modal.level.id}`, {
          name: name.trim(),
        });
      else await axios.delete(`/academic-levels/${modal.level.id}`);
      toast.success(
        `Academic level ${modal.mode === "add" ? "added" : modal.mode === "edit" ? "updated" : "deleted"} successfully.`,
      );
      setNotice(
        `Academic level ${modal.mode === "add" ? "added" : modal.mode === "edit" ? "updated" : "deleted"} successfully.`,
      );
      setModal(null);
      await load();
    } catch (err) {
      toast.error(
        errorMessage(err, "Could not save your changes. Please try again."),
      );
      setFormError(
        errorMessage(err, "Could not save your changes. Please try again."),
      );
    } finally {
      busy.current = false;
      setSaving(false);
    }
  };
  const visible = levels.filter((level) =>
    level.name.toLowerCase().includes(search.trim().toLowerCase()),
  );
  const surface = isDarkMode
    ? "border-white/15 bg-[#1e1e1e] text-white"
    : "border-slate-200 bg-white text-slate-900";
  const input = `w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "border-white/20 bg-[#292929] text-white" : "border-slate-300 bg-white text-slate-900"}`;
  return (
    <div
      className={`min-h-screen p-4 lg:p-8 ${isDarkMode ? "bg-[#292929] text-white" : "bg-slate-50 text-slate-900"}`}
    >
      <section
        className={`mt-10 rounded-2xl border p-4 shadow-sm lg:mt-0 lg:p-6 ${surface}`}
      >
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Academic Levels</h1>
            <p className="mt-1 text-sm opacity-70">
              Manage the academic levels used by students and courses.
            </p>
          </div>
          <button
            onClick={() => open("add")}
            className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-3 font-medium text-white hover:bg-blue-700"
          >
            <IoMdAdd aria-hidden="true" />
            Add Academic Level
          </button>
        </div>
        <label className="sr-only" htmlFor="academic-level-search">
          Search academic levels
        </label>
        <input
          id="academic-level-search"
          className={input}
          placeholder="Search academic levels"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        {notice && (
          <p
            role="status"
            className="mt-4 text-emerald-600 dark:text-emerald-400"
          >
            {notice}
          </p>
        )}
        <div className=" h-[720px] overflow-hidden">
          {loading ? (
            <div className="absolute inset-0 z-10 flex h-full w-full items-center justify-center">
              <ClipLoader color="#2264E5" size={50} />
            </div>
          ) : error ? (
            <div
              role="alert"
              className="flex h-full items-center justify-center text-red-500"
            >
              {error}

              <button onClick={load} className="ml-3 underline">
                Retry
              </button>
            </div>
          ) : (
            <div className="h-full animate-[fadeIn_0.4s_ease-out] overflow-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-400/25">
                    <th className="px-3 py-3">Name</th>
                    <th className="px-3 py-3 text-right">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {visible.map((level) => (
                    <tr
                      key={level.id}
                      className={`group border-b border-slate-400/20 transition-all duration-200 ${
                        isDarkMode
                          ? "hover:bg-white/[0.04]"
                          : "hover:bg-[#2264E5]/[0.04]"
                      }`}
                    >
                      <td className="px-3 py-4">{level.name}</td>

                      <td className="px-3 py-3">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => open("edit", level)}
                            aria-label={`Edit ${level.name}`}
                            className="group flex h-9 items-center overflow-hidden rounded-lg px-2 text-blue-500 transition-all duration-300 hover:bg-blue-500/10"
                          >
                            <MdEdit
                              aria-hidden="true"
                              className="shrink-0 text-lg"
                            />

                            <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-300 group-hover:ml-2 group-hover:max-w-16 group-hover:opacity-100">
                              Edit
                            </span>
                          </button>

                          <button
                            onClick={() => open("delete", level)}
                            aria-label={`Delete ${level.name}`}
                            className="group flex h-9 items-center overflow-hidden rounded-lg px-2 text-red-500 transition-all duration-300 hover:bg-red-500/10"
                          >
                            <MdOutlineDelete
                              aria-hidden="true"
                              className="shrink-0 text-lg"
                            />

                            <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-300 group-hover:ml-2 group-hover:max-w-20 group-hover:opacity-100">
                              Delete
                            </span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {!visible.length && (
                <div className="flex h-[300px] items-center justify-center opacity-70">
                  {search.trim()
                    ? "No matching academic levels."
                    : "No academic levels yet. Add one to get started."}
                </div>
              )}
            </div>
          )}
        </div>
      </section>
      {modal && (
        <Dialog open onClose={close} className="relative z-[60]">
          <div
            className="fixed inset-0 bg-slate-950/65 backdrop-blur-sm"
            aria-hidden="true"
          />
          <div className="fixed inset-0 flex items-center justify-center overflow-y-auto p-4">
            <DialogPanel
              className={`w-full max-w-md rounded-2xl border p-6 shadow-xl ${surface}`}
            >
              <DialogTitle className="text-xl font-bold">
                {modal?.mode === "delete"
                  ? "Delete academic level"
                  : modal?.mode === "edit"
                    ? "Edit Academic Level"
                    : "Add Academic Level"}
              </DialogTitle>
              <form onSubmit={submit} className="mt-4 space-y-4" noValidate>
                {modal?.mode === "delete" ? (
                  <Description>
                    Delete �{modal.level.name}�? Associated users and courses
                    will be left without an academic level.
                  </Description>
                ) : (
                  <div>
                    <label
                      htmlFor="academic-level-name"
                      className="mb-2 block text-sm font-medium"
                    >
                      Name
                    </label>
                    <input
                      autoFocus
                      id="academic-level-name"
                      value={name}
                      onChange={(event) => {
                        setName(event.target.value);
                        setFormError("");
                      }}
                      disabled={saving}
                      className={input}
                      aria-invalid={Boolean(formError)}
                      aria-describedby={
                        formError ? "academic-level-error" : undefined
                      }
                    />
                  </div>
                )}
                {formError && (
                  <p
                    id="academic-level-error"
                    role="alert"
                    className="text-sm text-red-500"
                  >
                    {formError}
                  </p>
                )}
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    disabled={saving}
                    onClick={close}
                    className="rounded-xl border border-slate-400/30 px-4 py-2 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className={`rounded-xl px-4 py-2 text-white disabled:opacity-50 ${modal?.mode === "delete" ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"}`}
                  >
                    {saving
                      ? "Saving..."
                      : modal?.mode === "delete"
                        ? "Delete"
                        : "Save"}
                  </button>
                </div>
              </form>
            </DialogPanel>
          </div>
        </Dialog>
      )}
    </div>
  );
}
